import React, { useCallback, useEffect, useState } from "react";
import type {
  CatalogueBottomEndFormValues,
  CatalogueCoilFrameFormValues,
  CataloguePowerSourceFormValues,
} from "../../../web-app";
import {
  buildBottomEndFormErrors,
  buildCoilFrameFormErrors,
  buildPowerSourceFormErrors,
  normalizeBottomEndValues,
  normalizeCoilFrameValues,
  normalizePowerSourceValues,
} from "../domain/catalogueModal";
import "./catalogue-item-modal.css";
import CcButton from "../components/CcButton";

export type CatalogueItemKind = "powerSource" | "coilFrame" | "bottomEnd";

export type CatalogueItemModalValues =
  | CataloguePowerSourceFormValues
  | CatalogueCoilFrameFormValues
  | CatalogueBottomEndFormValues;

export interface CatalogueItemModalProps {
  open: boolean;
  kind: CatalogueItemKind;
  mode: "create" | "edit";
  initialValues: CatalogueItemModalValues;
  existingValues?: CatalogueItemModalValues;
  onClose: () => void;
  onSubmit: (values: CatalogueItemModalValues) => void;
}

function modalTitle(kind: CatalogueItemKind, mode: "create" | "edit"): string {
  const labels: Record<CatalogueItemKind, string> = {
    powerSource: "Power Source",
    coilFrame: "Coil Frame",
    bottomEnd: "Bottom End",
  };
  const action = mode === "create" ? "Add" : "Edit";
  return `${action} ${labels[kind]}`;
}

function submitLabel(kind: CatalogueItemKind, mode: "create" | "edit"): string {
  const labels: Record<CatalogueItemKind, string> = {
    powerSource: "Power Source",
    coilFrame: "Coil Frame",
    bottomEnd: "Bottom End",
  };
  const action = mode === "create" ? "Add" : "Save";
  return `${action} ${labels[kind]}`;
}

const CatalogueItemModal = ({
  open,
  kind,
  mode,
  initialValues,
  existingValues,
  onClose,
  onSubmit,
}: CatalogueItemModalProps) => {
  const [values, setValues] = useState<CatalogueItemModalValues>(initialValues);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setValues(initialValues);
    setFieldErrors({});
  }, [open, initialValues]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const setField = useCallback((key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleSubmit = () => {
    if (kind === "powerSource") {
      const row = values as CataloguePowerSourceFormValues;
      const errors = buildPowerSourceFormErrors(row);
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;
      onSubmit(
        normalizePowerSourceValues(row, existingValues as CataloguePowerSourceFormValues | undefined),
      );
      return;
    }

    if (kind === "coilFrame") {
      const row = values as CatalogueCoilFrameFormValues;
      const errors = buildCoilFrameFormErrors(row);
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;
      onSubmit(normalizeCoilFrameValues(row));
      return;
    }

    const row = values as CatalogueBottomEndFormValues;
    const errors = buildBottomEndFormErrors(row);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    onSubmit(normalizeBottomEndValues(row, existingValues as CatalogueBottomEndFormValues | undefined));
  };

  if (!open) return null;

  return (
    <div
      className="dialog-scrim"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="dialog-card catalogue-item-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="catalogue-item-modal-title"
      >
        <div className="dialog-header catalogue-item-modal__header">
          <h2 className="dialog-title" id="catalogue-item-modal-title">
            {modalTitle(kind, mode)}
          </h2>
          <CcButton variant="icon" aria-label="Close modal" onClick={onClose}>
            ✕
          </CcButton>
        </div>

        <div className="dialog-body catalogue-item-modal-body">
          <form
            id="catalogue-item-modal-form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit();
            }}
          >
            <div className="add-catalogue-form">
              {kind === "powerSource" && (
                <>
                  <TextField
                    id="ps-id"
                    label="ID"
                    value={(values as CataloguePowerSourceFormValues).id}
                    error={fieldErrors.id}
                    onChange={(value) => setField("id", value)}
                  />
                  <TextField
                    id="ps-name"
                    label="Name"
                    value={(values as CataloguePowerSourceFormValues).name}
                    error={fieldErrors.name}
                    onChange={(value) => setField("name", value)}
                  />
                  <NumericField
                    id="ps-rating"
                    label="Rating, kVA"
                    step="0.1"
                    value={(values as CataloguePowerSourceFormValues).powerSourceRatingKva}
                    error={fieldErrors.powerSourceRatingKva}
                    onChange={(value) => setField("powerSourceRatingKva", value)}
                  />
                  <NumericField
                    id="ps-frequency"
                    label="Frequency, Hz"
                    step="0.1"
                    value={(values as CataloguePowerSourceFormValues).powerSourceFrequencyHz}
                    error={fieldErrors.powerSourceFrequencyHz}
                    onChange={(value) => setField("powerSourceFrequencyHz", value)}
                  />
                  <NumericField
                    id="ps-voltage"
                    label="Max Voltage, V"
                    step="0.1"
                    value={(values as CataloguePowerSourceFormValues).maximumPowerSourceVoltageV}
                    error={fieldErrors.maximumPowerSourceVoltageV}
                    onChange={(value) => setField("maximumPowerSourceVoltageV", value)}
                  />
                  <NumericField
                    id="ps-current"
                    label="Max Current, A"
                    step="0.1"
                    value={(values as CataloguePowerSourceFormValues).maximumPowerSourceCurrentA}
                    error={fieldErrors.maximumPowerSourceCurrentA}
                    onChange={(value) => setField("maximumPowerSourceCurrentA", value)}
                  />
                </>
              )}

              {kind === "coilFrame" && (
                <>
                  <TextField
                    id="cf-id"
                    label="ID"
                    value={(values as CatalogueCoilFrameFormValues).id}
                    error={fieldErrors.id}
                    onChange={(value) => setField("id", value)}
                  />
                  <TextField
                    id="cf-name"
                    label="Name"
                    value={(values as CatalogueCoilFrameFormValues).name}
                    error={fieldErrors.name}
                    onChange={(value) => setField("name", value)}
                  />
                  <NumericField
                    id="cf-size"
                    label="Frame Size, in"
                    step="0.5"
                    value={(values as CatalogueCoilFrameFormValues).coilFrameSizeInches}
                    error={fieldErrors.coilFrameSizeInches}
                    onChange={(value) => setField("coilFrameSizeInches", value)}
                  />
                  <NumericField
                    id="cf-diameter"
                    label="Inside Diameter, mm"
                    step="0.1"
                    value={(values as CatalogueCoilFrameFormValues).coilFrameInsideDiameterMm}
                    error={fieldErrors.coilFrameInsideDiameterMm}
                    onChange={(value) => setField("coilFrameInsideDiameterMm", value)}
                  />
                </>
              )}

              {kind === "bottomEnd" && (
                <>
                  <TextField
                    id="be-name"
                    label="Name"
                    value={(values as CatalogueBottomEndFormValues).name}
                    error={fieldErrors.name}
                    onChange={(value) => setField("name", value)}
                  />
                  <NumericField
                    id="be-turns"
                    label="Turns"
                    step="1"
                    value={(values as CatalogueBottomEndFormValues).numberOfTurns}
                    error={fieldErrors.numberOfTurns}
                    onChange={(value) => setField("numberOfTurns", value)}
                  />
                  <TextField
                    id="be-spacing"
                    label="Contact Spacing mm (comma-separated, count = turns − 1)"
                    hint="e.g. 75, 75, 75, 75, 75 for 6 turns"
                    value={(values as CatalogueBottomEndFormValues).contactCenterSpacingsMm}
                    error={fieldErrors.contactCenterSpacingsMm}
                    onChange={(value) => setField("contactCenterSpacingsMm", value)}
                  />
                  <NumericField
                    id="be-width"
                    label="Bottom End Width, mm"
                    step="0.1"
                    value={(values as CatalogueBottomEndFormValues).bottomEndWidthMm}
                    error={fieldErrors.bottomEndWidthMm}
                    onChange={(value) => setField("bottomEndWidthMm", value)}
                  />
                </>
              )}
            </div>
          </form>
        </div>

        <div className="dialog-footer">
          <CcButton variant="ghost" onClick={onClose}>
            Cancel
          </CcButton>
          <CcButton variant="primary" type="submit" form="catalogue-item-modal-form">
            {submitLabel(kind, mode)}
          </CcButton>
        </div>
      </div>
    </div>
  );
};

function TextField({
  id,
  label,
  hint,
  value,
  error,
  onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="add-catalogue-field">
      <label htmlFor={id} className="add-catalogue-field__label">
        {label}
      </label>
      {hint ? <span className="add-catalogue-field__hint">{hint}</span> : null}
      <input
        id={id}
        className="add-catalogue-field__input"
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <span className="field-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

function NumericField({
  id,
  label,
  step,
  value,
  error,
  onChange,
}: {
  id: string;
  label: string;
  step: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="add-catalogue-field">
      <label htmlFor={id} className="add-catalogue-field__label">
        {label}
      </label>
      <input
        id={id}
        className="add-catalogue-field__input pih-numeric-input"
        type="number"
        step={step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <span className="field-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export default CatalogueItemModal;
